/** @module Camunda/FilmStrip */
define([
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/promise/all",
	"underscore",
	"csi/constants",
	"csi/html",
	"csi/middleware/attributes/attributeKeysService",
	"csi/session",
	"csi/state",
	"csi/table/Widget",
	"dojo/text!./FilmStrip.html",

	"csi/widget/Select",
	"csi/table/ToolbarFilmStripFind",
	"csi/table/ToolbarFilmStripGroup"
], function(declare, fx, domClass, domStyle, all, _, constants, html, {getFirstAttributeKey}, session, state, TableWidget, template) {

	/**
	 * Film Strip widget for use with Camunda MyTasks view and another Camunda view(s)
	 */
	const LEFT_FRAME_WIDTH = 297;
	const {LOCATION_FILMSTRIP} = session;

	return declare([TableWidget], {
		allowAutomaticHeight: true,
		bRemoveGroupsEmptyTable: false,
		flexHeight: true,
		flexHeightPropagetesToChildren: false,
		plugins: ["FilmStripFind", "FilmStripGroup"],
		pluginsRight: ["FilmStripFind", "FilmStripGroup"],
		querySize: 0,
		querySizeFixed: true,
		restrictGroupSelection: true,
		tableHeight: constants.TABLE_HEIGHT_AUTO,
		templateString: template,

		// attach points
		filmstripBackNode: null,
		filmstripContentNode: null,
		filmstripHeaderNode: null,
		filmstripHeaderInsertNode: null,
		filmstripLabelNode: null,
		filmstripLeftHeaderNode: null,
		filmstripLeftNode: null,
		filmstripRightNode: null,
		filmstripSlideNode: null,

		/** @property {string} filmstripContentView - view id to use to fill filmstrip content area */
		filmstripContentView: "",

		/** @property {string} filmstripHeaderView - view id to use to fill filmstrip header area */
		filmstripHeaderView: "",

		/** @property {string} filmstripLabel - label to be displayed in filmstrip mode above toolbar */
		filmstripLabel: "",

		/** @property {boolean} filmstripMode - if set to true, then transition to filmstrip mode */
		filmstripMode: false,

		/** @property {Memory} filmstripPluginStore - Store for film drop down */
		filmstripPluginStore: null,

		/** @propery {string} filmstripSearchText - text to filter results by */
		filmstripSearchText: "",

		/** @property {string} filmstripSelection - query result id of selected item or group */
		filmstripSelection: "",

		/** @property {boolean} showFilmstripSearch - set to true when search icon pressed to enter search text */
		showFilmstripSearch: false,

		/** @property {string} _filmstripAffectTableHeader - set to true if table header is not hidden by default so that header is hidden when transitions to filmstrip mode */
		_filmstripAffectTableHeader: false,

		/** @property {Widget} _filmstripContentView - filmstrip content view */
		_filmstripContentView: null,

		/** @property {Widget} _filmstripHeaderView - filmstrip header view */
		_filmstripHeaderView: null,

		/** @property {boolean} _filmstripInitialized - done initializing filmstrip */
		//_filmstripInitialized: null,

		/** @property {boolean} _inFilmstripMode - if true then in filmstrip mode */
		_inFilmstripMode: false,

		/** @property {string} _originalFilmstripLabel - original label before starting action to show new label and pane */
		_originalFilmstripLabel: "",

		/**
		 * @override
		 * Table row selections should not trigger the filmstrip to open
		 */
		allowsSelections: function() {
			return false;
		},

		/**
		 * @override
		 * If in filmstrip mode, then return fixed width
		 */
		getAvailableTableWidth: function(ignorePopup) {
			return this.inFilmstripMode() ? LEFT_FRAME_WIDTH : this.inherited(arguments);
		},

		/**
		 * Return query result for current filmstrip selection
		 * @return {Result|null}
		 */
		getFilmstripQueryResult: function() {
			return this.filmstripSelection ? this.getQueryResult(this.filmstripSelection) : null;
		},

		/**
		 * Return selected node
		 * @return {Node|null}
		 */
		getFilmstripSelectedNode: function() {
			const qr = this.getFilmstripQueryResult();
			return qr && qr.getNode();
		},

		/**
		 * Return url of selected node
		 * @return {string|null}
		 */
		getFilmstripSelectedUrl: function() {
			const qr = this.getFilmstripQueryResult();
			return qr && qr.url;
		},

		/**
		 * Return true if showing
		 * @return {boolean}
		 */
		inFilmstripMode: function() {
			return this._inFilmstripMode;
		},

		/**
		 * Return true if filmstrip in slide mode
		 * @return {boolean}
		 */
		inFilmstripSlideMode: function() {
			return !!this._originalFilmstripLabel;
		},

		/**
		 * Extension point to configure parameters for content view based on current selection
		 * @return {Object|null}
		 */
		onFilmstripContentViewParams: function() {
			return null;
		},

		/**
		 * Extension point to configure parameters for header view based on current selection
		 * @return {Object|null}
		 */
		onFilmstripHeaderViewParams: function() {
			return null;
		},

		/**
		 * @override
		 * Add selection styling if row is the filmstrip selection
		 */
		onRowStyling: function(row, qr) {
			if (qr && (qr.id === this.filmstripSelection)) {
				this.select(row, true);
			}

			this.inherited(arguments);
		},

		/**
		 * @override 
		 * If a row is selected, and in filmstrip mode, then switch location
		 */
		onSelectRow: function(tr, bSelected) {
			if (bSelected && this.inFilmstripMode()) {
				const qr = this.getQueryResultByDomNode(tr);
				if (qr) {
					this.set("filmstripSelection", qr.id);
				}
			}
		},

		/**
		 * @override
		 * Check for url "Filmstrip" parameter value change and apply the change
		 */
		onTabSwitch: async function() {
			// force refresh on tab change - capture filmstrip value before refresh so it is not lost
			const value = session.getLocationValue(LOCATION_FILMSTRIP);
			await this.refreshData();
			this._checkFilmstripParam(value);
		},

		/**
		 * Extension point to react to toggling on/off of filmstrip mode
		 * @param {boolean} toggled
		 */
		onToggleFilmstrip: function(toggled) { },

		/**
		 * Extension point called when filmstrip is either closed or slid back 
		 */
		onUnslideFilmstrip: function() { },

		/**
		 * @override
		 * Fade in view when done loading
		 */
		onViewEnd: function() {
			this.inherited(arguments);
			this._filmstripFadeIn();
		},

		/**
		 * @override
		 * Fix first column width to that which is required when filmstrip mode is on
		 */
		onViewStart: function(dfds) {
			this.inherited(arguments);

			const firstKey = getFirstAttributeKey(this.attributeKeys);
			this.fixedColumnWidths = this.fixedColumnWidths || {};
			this.fixedColumnWidths[firstKey] = this.fixedColumnWidths[firstKey] || LEFT_FRAME_WIDTH;
		},

		/**
		 * @override
		 * Add CSS to left side of filmstrip if a focused view
		 */
		postCreate: function() {
			if (this.bFocused) {
				$(this.filmstripLeftNode).addClass("csi-focused-view-and-action");
				$(this.filmstripRightNode).addClass("csi-flex-column");
				domStyle.set(this.containerNode, "overflow-x", "hidden");
				html.setPixels(this.containerNode, html.WIDTH, LEFT_FRAME_WIDTH);
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Set table height to use full page
		 */
		postMixInProperties: function() {
			if (this.bFocused) {
				this.allowAutomaticHeight = true;
			}

			if (!this.hideHeader) {
				this._filmstripAffectTableHeader = true;
			}

			this._watchLocation();
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Resize child views when this view is resized
		 */
		resize: function() {
			this.inherited(arguments);
			this._resizeFilmstripViews();
		},

		/**
		 * Slide pane over existing filmstrip
		 * @param {string} label - new label for filmstrip
		 * @param {_WidgetBase} widget
		 */
		slideFilmstrip: function(label, widget) {
			this._setFilmstripSlideVisibility(true);
			this._originalFilmstripLabel = this.filmstripLabel;
			this.set("filmstripLabel", label);

			widget.placeAt(this.filmstripSlideNode);
			widget.startup();
		},

		/**
		 * @override
		 * If in slide mode, notify listeners it is ending
		 */
		uninitialize: function() {
			if (this.inFilmstripSlideMode()) {
				this.onUnslideFilmstrip();
			}

			this.inherited(arguments);
		},

		/**
		 * Restore filmstrip pane to original state
		 */
		unslideFilmstrip: function() {
			if (this.inFilmstripSlideMode()) {
				this.set("filmstripLabel", this._originalFilmstripLabel);
				this._originalFilmstripLabel = null;

				this._setFilmstripSlideVisibility(false);
				html.clear(this.filmstripSlideNode, true);

				this.onUnslideFilmstrip();
			}
		},

		/**
		 * Create filmstrip content view
		 * @param {Result} qr
		 * @param {DOMNode} domNode
		 * @return {Promise}
		 */
		_createFilmstripContentView: function(qr, domNode) {
			const params = this.onFilmstripContentViewParams(qr);

			return this._insertAndCreateView(this.filmstripContentView, qr.getNode(), domNode, {
				flexHeight: true,
				...params
			});
		},

		/**
		 * Create filmstrip header view
		 * @param {Result} qr
		 * @param {DOMNode} domNode
		 * @return {Promise}
		 */
		_createFilmstripHeaderView: function(qr, domNode) {
			const params = this.onFilmstripHeaderViewParams(qr);
			return this._insertAndCreateView(this.filmstripHeaderView, qr.getNode(), domNode, params);
		},

		/**
		 * Create header and content views
		 */
		_createFilmstripViews: function() {
			const {filmstripContentNode, filmstripContentView, filmstripHeaderInsertNode, filmstripHeaderNode, filmstripHeaderView} = this;
			const dfds = [];
			const qr = this.getFilmstripQueryResult();
			const node = qr && qr.getNode();

			html.showWait(true, true);

			// ensure that view containers are empty; it is possible that header and content views are still in process of being loaded
			// and the class members are not yet set
			// FIX: it would probably be better to synchronously create views and assign them, rather than waiting for them to be loaded;
			// this is possible since all JS files are in same bundle and are loaded synchronously, but not 100% sure we will stick with
			// that approach, so holding off on changing the view loading process
			html.clear(filmstripHeaderInsertNode, true);
			html.clear(filmstripContentNode, true);
			this._filmstripHeaderView = null;
			this._filmstripContentView = null;

			if (node) {
				// create header view
				if (filmstripHeaderView) {
					dfds.push(this._createFilmstripHeaderView(qr, filmstripHeaderInsertNode).then(view => {
						// if view was deleted in the process of being created, then do not set
						if (view.isValidView()) {
							this._filmstripHeaderView = view;
						}
					}));
				}

				// create content view
				if (filmstripContentView) {
					dfds.push(this._createFilmstripContentView(qr, filmstripContentNode).then(view => {
						// if view was deleted in the process of being created, then do not set
						if (view.isValidView()) {
							this._filmstripContentView = view;
						}
					}));
				}
			}

			this.when(all(dfds), () => {
				html.disablePageContentScrolling(false);
				fx.fadeIn({node: filmstripHeaderNode}).play();
				fx.fadeIn({node: filmstripContentNode}).play();
				html.showWait(false);
			});
		},

		/**
		 * Delete header and content views
		 */
		_deleteFilmstripViews: function() {
			if (this._filmstripHeaderView) {
				this._filmstripHeaderView.destroyView();
				this._filmstripHeaderView = null;
			}

			if (this._filmstripContentView) {
				this._filmstripContentView.destroyView();
				this._filmstripContentView = null;
			}
		},

		/**
		 * Return DOMNode that has scrollable table content
		 * @return {DOMNode}
		 */
		_getFilmstripScrollNode: function() {
			return this.forceFullHeight ? this.containerNode : this.scrollNode;
		},

		/**
		 * Slide back button callback
		 * @param {Event} e
		 */
		_onSlideBack: function(e) {
			this.unslideFilmstrip();
		},

		/**
		 * Close button callback
		 * @param {Event} e
		 */
		_onContentClose: function(e) {
			this.set("filmstripMode", false);

			if (this.filmstripSelection) {
				this.set("filmstripSelection", "");
			} else {
				this._refreshFilmstripViews();
			}
		},

		/**
		 * @override
		 * This view's url parameters were changed while tab was active, process changes
		 */
		_onLocationChange(value) {
			this._checkFilmstripParam(value, true);
		},

		/**
		 * Check url parameter "Filmstrip" and apply it's value
		 * @param {string} [filmstripValue]
		 * @param {boolean} [bRefresh]
		 */
		_checkFilmstripParam: async function(filmstripValue, bRefresh) {
			filmstripValue = filmstripValue || session.getLocationValue(LOCATION_FILMSTRIP);

			// reset location value so clicking on notification will trigger another change event in case user selected another task in the meantime
			session.setLocationActual(LOCATION_FILMSTRIP, null, null, (value) => this._onLocationChange(value));

			if (!filmstripValue || (filmstripValue === this.filmstripSelection)) {
				return;
			}

			if (bRefresh && !this.getQueryResult(filmstripValue)) {
				await this.refreshData();
			}

			if (this.getQueryResult(filmstripValue)) {
				this.set("filmstripSelection", filmstripValue);
			}
		},

		/**
		 * Resize child views
		 */
		_resizeFilmstripViews: function() {
			const {_filmstripContentView, _filmstripHeaderView} = this;

			if (_filmstripHeaderView) {
				_filmstripHeaderView.resize();
			}

			if (_filmstripContentView) {
				_filmstripContentView.resize();
			}
		},

		/**
		 * Update toolbar plugins when showing or hiding search box
		 * @param {boolean} value
		 */
		_setShowFilmstripSearchAttr: function(value) {
			this._set("showFilmstripSearch", value);
			this.toolbarNode.enablePlugins();
		},

		/**
		 * Turn filmStrip mode on/off
		 * @return {Promise}
		 */
		_toggleFilmStripMode: function() {
			const bToggled = this.filmstripMode;

			if (this.inFilmstripMode() == bToggled) {
				return null;
			}

			const {domNode} = this;
			const scrollNode = this._getFilmstripScrollNode();
			const bChangeHeader = this._filmstripAffectTableHeader;

			this._inFilmstripMode = bToggled;
			html.disablePageContentScrolling(true);

			if (bChangeHeader) {
				this.hideHeader = bToggled;
			}

			if (bToggled) {
				// Hide all table columns but first one after resize
				domStyle.set(scrollNode, "overflow-x", "hidden");
			} else {
				// need to ensure that first column is correct width
				this._fixColumnWidths();
			}

			// Prevent horizontal scroller during the animation
			this._disablePageViewScrolling(false);

			return new Promise((resolve, reject) => {
				fx.animateProperty({
					node: scrollNode,
					duration: 300,
					properties: {
						width: bToggled ? LEFT_FRAME_WIDTH : domNode.offsetWidth
					},
					onEnd: () => {
						if (bChangeHeader) {
							domClass.toggle(this.tableContainerNode, "csi-table-no-header", bToggled); // Show/Hide table header
						}

						domClass.toggle(domNode, "csi-fs-on", bToggled); // Toggle film strip styling
						html.setVisible(this.filmstripHeaderNode, bToggled); // Show upper right widget pane
						html.setVisible(this.filmstripContentNode, bToggled); // Show switch board content node
						this._disablePageViewScrolling(false);

						if (bToggled) {
							this._fixColumnWidths();
						}

						// Make selected row visible
						var selected = this.getSelectedRows();
						if (selected.length) {
							html.scrollIntoViewVertically(scrollNode, selected[0]);
						}

						if (!bToggled) {
							domStyle.set(scrollNode, "overflow-x", ""); // Show all table columns
							this._forceResize(bToggled);
							html.disablePageContentScrolling(false);
						}
						this.toolbarNode.enablePlugins();

						this.onToggleFilmstrip(bToggled);
						resolve();
					}
				}).play();
			});
		},

		/**
		 * Fade in view once everything is loaded
		 */
		_filmstripFadeIn: function() {
			if (!this._filmstripInitialized) {
				this._filmstripInitialized = true;
				this._enableFilmstrip();
				this.watch("filmstripSelection", this._onWatchFilmstripSelection.bind(this));
				this._checkFilmstripParam();
			} else if (this.filmstripSelection) {
				if (this.getFilmstripQueryResult()) {
					// if reloading and filmstrip exists but does not need to change, then still resize just in case;
					// this covers case of handling reloading view on a tab switch when browser may have changed sizes
					// since view was last resized
					this._resizeFilmstripViews();
				} else {
					this.set("filmstripSelection", "");
				}
			}

			fx.fadeIn({node: this.domNode}).play();
		},

		/**
		 * Resize without timeout
		 * @param {boolean} bToggled
		 */
		_forceResize: function(bToggled) {
			state.set("forceResize", true);
			this.allowAutomaticHeight = !bToggled;
			this.resize();
			state.set("forceResize", false);
		},

		/**
		 * Disable Page Content scroll bar to appear during transition
		 * @param {boolean} bDisable - if true, scrolling will be disabled
		 */
		_disablePageViewScrolling: function(bDisable) {
			domStyle.set(this.domNode, "overflow", bDisable ? "hidden" : "");
		},

		/**
		 * Enable filmstrip mode for current selection
		 */
		_enableFilmstrip: function() {
			const id = this.filmstripSelection;
			const refNode = id && this.getRefNodeById(id);

			if (refNode) {
				// TODO: do we need to make sure row is expanded - if group is collapsed, and it is only one with remaining tasks, do we expand it?

				// select table row
				this.select(refNode, true);
				this.set("filmstripMode", true);
			} else {
				// reset selection if not found
				this.set("filmstripSelection", "");
			}

			if (this.filmstripMode) {
				// ensure that header and content nodes are faded out
				$(this.filmstripHeaderNode).css("opacity", 0);
				$(this.filmstripContentNode).css("opacity", 0);

				this.when(this._toggleFilmStripMode(), this._createFilmstripViews.bind(this));
			}
		},

		/**
		 * @override
		 * If in filmstrip mode, then return left frame width
		 */
		_getSpecColumnWidth: function(spec, fixedWidth, isFirstColumn) {
			return isFirstColumn && this.inFilmstripMode() ? LEFT_FRAME_WIDTH : this.inherited(arguments);
		},

		/**
		 * React to changes from filmstrip selection
		 * @param {string} attr
		 * @param {string} oldValue
		 * @param {string} newValue
		 */
		_onWatchFilmstripSelection: function(attr, oldValue, newValue) {
			if (oldValue) {
				// remove CSS from previously selected row
				this.select(this.getRefNodeById(oldValue), false);
			} else if (!this.inFilmstripMode()) {
				// if not currently in filmstrip mode, it is possible a row has select styling but not yet considered
				// the filmstrip selection. We need to clear such styling now.
				this.unselectAll();
			}

			this._refreshFilmstripViews(oldValue && newValue);
		},

		/**
		 * Redraw filmstrip header and content views for current selection
		 * @param {boolean} [fadeOut] - set to true to fade out old views (when there was a previous selection)
		 */
		_refreshFilmstripViews: function(fadeOut) {
			const self = this;
			var p1;
			var p2;

			html.showWait(true, true);

			if (fadeOut) {
				// fade out views if there is a new selection
				p1 = new Promise(function(resolve, reject) {
					fx.fadeOut({
						node: self.filmstripHeaderNode,
						onEnd: resolve
					}).play();
				});

				p2 = new Promise(function(resolve, reject) {
					fx.fadeOut({
						node: self.filmstripContentNode,
						onEnd: resolve
					}).play();
				});
			} else {
				// nothing to fade otherwise
				p1 = Promise.resolve();
				p2 = Promise.resolve();
			}

			this.when(Promise.all([p1, p2]), function() {
				if (self.getFilmstripQueryResult()) {
					self._deleteFilmstripViews();
					self._enableFilmstrip();

					// always switch to searching mode if the search text is not null, otherwise, keep the normal mode
					self.set("showFilmstripSearch", !!self.get("filmstripSearchText"));
				} else {
					// only delete the header and content views after going back to viewing full table
					self.when(self._toggleFilmStripMode(), function() {
						self._deleteFilmstripViews();
						html.showWait(false);
					});
				}
			});
		},

		/**
		 * Map filmstrip label to node in view header
		 */
		_setFilmstripLabelAttr: {node: "filmstripLabelNode", type: "innerText"},

		/**
		 * Set visibility of nodes in left pane to show/hide slide pane
		 * @param {boolean} bShow
		 */
		_setFilmstripSlideVisibility: function(bShow) {
			html.setVisible(this.toolbarNode, !bShow);
			html.setVisible(this.containerNode, !bShow);
			html.setVisible(this.actionsNode, !bShow);
			html.setVisible(this.filmstripSlideNode, bShow);
			html.setVisible(this.filmstripBackNode, bShow);
		},

		/**
		 * Set listener for "Filmstrip" url parameter change - make sure to release any existing filmstrip
		 * key so that you will not get "Filmstrip2" as key which will mess up notification links. Only
		 * one filmstrip can be active at one time.
		 */
		_watchLocation() {
			session.releaseLocationKey(LOCATION_FILMSTRIP);
			session.reserveLocationKey(LOCATION_FILMSTRIP, (value) => this._onLocationChange(value));
		}
	});
});